"use client";

import { List, Map } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentProps,
} from "react";
import MapView from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useGrantedGeolocation } from "@/hooks/useGrantedGeolocation";
import {
  useStationHubData,
  type StationHubDataset,
} from "@/hooks/useStationHubData";
import { useSearchParams } from "next/navigation";
import { calculateDistance } from "@/lib/api/products/geo";
import {
  applyHubParams,
  historyModeFor,
  hubDepth,
  hubStateKey,
  parseHubParams,
  type HubUrlState,
} from "@/lib/mobility/hub-url";
import { BIKE_CLASSES, type BikeClass } from "@/lib/mobility/bike-classes";
import {
  collectPresentFacets,
  countActiveAxes,
  EMPTY_AXES,
  isAxesEmpty,
  matchesAxes,
  type HubAxes,
  type PresentFacets,
} from "@/lib/mobility/bike-facets";
import {
  collectPartySizes,
  matchesParty,
  resolvePartySize,
} from "@/lib/mobility/camp-party";
import {
  hubKey,
  stationCategoryCode,
  type HubKind,
} from "@/lib/mobility/station-hub";
import {
  boxAround,
  toWorldId,
  usePlacePoint,
  type InitialLandmarkCountry,
  type PlacePoint,
  type ViewBox,
} from "@/lib/landmarks/client";
import type { SortOption } from "@/components/search/SortDialog";
import type { HubModelLink } from "@/lib/api/model-hub";
import type { Product } from "@/types";
import type { StationSummary } from "@/hooks/useStationHubData";
import { stationPathsFromNameSlugs } from "@/lib/seo/station-url";
import type { SupportedCurrency } from "@/lib/utils/currency";
import { CampingStationSheet } from "./CampingStationSheet";
import { CatalogSheet } from "./CatalogSheet";
import { GearSheet } from "./GearSheet";
import {
  HubDensityProvider,
  HubProvider,
  type HubSheet,
  type HubTranslate,
} from "./hub-context";
import { AxisPills } from "./AxisPills";
import { LandmarkPill } from "./LandmarkPill";
import {
  boundHubStations,
  mapAreaBoundsOf,
} from "@/lib/mobility/station-bounds";
import { hubScopeFromParams, hubScopeStationIds } from "@/lib/seo/hub-landing";
import { replaceSearchUrlShallow } from "@/lib/search/url-view-sync";
import {
  collectHubGear,
  extraGear,
  hubProductIds,
  shippableHubGear,
} from "@/lib/mobility/camping-gear";
import { useGearExtras } from "./useGearExtras";
import {
  createStationHoverStore,
  type StationHoverStore,
} from "./station-hover-store";
import { Chip, HUB_GUTTER, SegmentedTabs } from "./hub-ui";
import { ModelSheet } from "./ModelSheet";
import { NearbySheet } from "./NearbySheet";
import { StationBikesSheet } from "./StationBikesSheet";
import { useSnapSheet } from "./useSnapSheet";
import { WindowCapProvider } from "./useWindowedCount";
import { LandingBlocksSection } from "../LandingBlocksSection";
import { RelatedRentalsNav } from "../RelatedRentalsNav";
import type { RelatedRentals } from "../types";
import type { LandingBlocks } from "@/lib/seo/landing-blocks";

/**
 * 모바일 시트 스냅 — 검색 페이지 상품 드로어와 **같은 값**(0.15 / 0.5 / 1).
 * 0.5 는 지도에서 핀을 골랐을 때 — 지점 시트와 선택 핀을 함께 보이게 한다(10-06,
 * 상품 드로어와 같은 규칙). 목록에서 고르면 지금처럼 끝까지 펼친다.
 * 모듈 상수: 렌더마다 새 배열을 만들면 스냅 붙임이 깨진다.
 */
const SNAP_POINTS = [0.15, 0.5, 1] as const;
const PIN_SNAP = SNAP_POINTS[1];

/** URL 로 복원할 드로어 높이 — 펼침이면 1, 지점 시트만 열려 있으면 핀 높이. */
function snapFromUrl(url: HubUrlState): number | undefined {
  if (url.expanded) return 1;
  if (url.mode !== "catalog" && url.stationId) return PIN_SNAP;
  return undefined;
}

/**
 * 상품 목록(기본 탭 — 오토바이 차종 · 캠핑 장비)에 **들어올 때**의 드로어 높이 — 핀 높이(50%)라
 * 지도의 지점 핀과 목록이 함께 보인다(10-06, 캠핑 10-15). 첫 진입과, 뒤로가기로 이 화면에
 * 돌아올 때만 쓴다 — 탭을 직접 눌러 목록으로 오면 지금처럼 끝까지 펼친다(handleModeChange).
 */
function catalogEntrySnap(url: HubUrlState): number | undefined {
  return url.mode === "catalog" &&
    !url.modelKey &&
    !url.expanded
    ? PIN_SNAP
    : undefined;
}

/**
 * 지도 위에 띄운 필터 알약(60) + 칩 한 줄(40) 만큼 카메라 상단을 비운다 —
 * 칩이 한 줄로 합쳐져 상품 모드의 cameraPaddingTop(100) 과 같다.
 */
const FLOATING_CHROME_PX = 100;

/**
 * 모바일 첫 화면 미리보기(10-15) — 레이아웃 판정 전(SSR·첫 클라이언트 렌더) 드로어에 그리는
 * 목록 행 수. 50% 드로어를 채우는 정도면 된다(나머지는 판정 뒤 창 단위로 이어 붙인다).
 */
const MOBILE_PREVIEW_ROWS = 6;

/**
 * 거리 표기 임계값. 기준점이 이보다 멀면 "1162km" 같은 숫자만 나오므로 아예 뺀다.
 * 사용자 위치가 지도 중심에서 이만큼 떨어져 있으면 기준점도 지도 중심으로 되돌린다.
 */
const MAX_DISTANCE_KM = 200;

type HubMode = "map" | "catalog";

/** 지도 영역 검색(MAP_AREA) 범위를 이루는 주소 키 — "전체 지점 보기"가 지운다. */
const MAP_AREA_KEYS = ["ne_lat", "ne_lng", "sw_lat", "sw_lng", "location_search"];

/**
 * 허브 지도 — 데스크톱 카드 호버(외부 저장소)를 **여기서만** 구독해 핀 강조로 넘긴다.
 * 허브 본체가 구독하면 호버마다 목록 전체가 다시 그려진다(station-hover-store).
 */
function HubMap({
  hoverStore,
  ...props
}: ComponentProps<typeof MapView> & { hoverStore: StationHoverStore }) {
  const hovered = useSyncExternalStore(
    hoverStore.subscribe,
    hoverStore.get,
    () => null,
  );
  return <MapView {...props} highlightedProductId={hovered} />;
}

/** 캠핑 허브에 넘기는 바이크 축 값 — 늘 비어 있다(참조 고정, 알약을 그리지 않는다). */
const NO_PRESENT_FACETS: PresentFacets = {
  makers: [],
  types: [],
  rides: [],
  trans: [],
};

/**
 * URL 파라미터가 가리키는 시트. 가리키는 지점·모델이 지금 결과에 없으면
 * (필터가 바뀌었거나 남의 링크) 조용히 목록으로 떨어진다.
 */
function sheetFromUrl(
  url: HubUrlState,
  data: { stationsById: Map<string, unknown>; models: Array<{ key: string }> },
  kind: HubKind,
): HubSheet {
  if (url.mode === "catalog") {
    // 캠핑의 [장비] 탭은 검색 결과 카드 목록이라 모델 시트가 없다.
    return kind === "mobility" &&
      url.modelKey &&
      data.models.some((m) => m.key === url.modelKey)
      ? { kind: "model", modelKey: url.modelKey }
      : { kind: "catalog" };
  }
  return url.stationId && data.stationsById.has(url.stationId)
    ? { kind: "bikes", stationId: url.stationId }
    : { kind: "nearby" };
}

/** URL 상태 → 필터 축(모두 URL 이 정본). */
function axesFromUrl(url: HubUrlState): HubAxes {
  return {
    classKey: url.classKey ?? null,
    makers: url.makers ?? [],
    types: url.types ?? [],
    rides: url.rides ?? [],
    trans: url.trans ?? null,
    parties: url.parties ?? [],
  };
}

/**
 * 허브 종류에 맞는 축만 남긴다 — 캠핑은 인원만, 모빌리티는 바이크 축만. URL 에 남은
 * 반대쪽 축(다른 허브에서 넘어온 링크 등)이 목록을 걸러 버리지 않게 한다.
 */
function axesForKind(axes: HubAxes, kind: HubKind): HubAxes {
  if (kind === "camping") {
    return axes.parties.length === 0
      ? EMPTY_AXES
      : { ...EMPTY_AXES, parties: axes.parties };
  }
  return axes.parties.length > 0 ? { ...axes, parties: [] } : axes;
}

interface StationHubProps {
  /**
   * 허브 종류(기본 mobility). 캠핑은 [캠핑장 | 장비] 토글·인원 축만 있고,
   * [장비] 탭은 `products`(일반 검색 결과)를 상품 카드로 그린다.
   */
  kind?: HubKind;
  /**
   * 카테고리·가격·정렬·이용방식이 적용된 상품 배열 (spec §1).
   * `initialHubDataset` 이 있으면 쓰이지 않는다(데이터셋이 단일 소스) — 데이터셋
   * 조회가 실패했을 때의 폴백 경로로만 남는다.
   */
  products: Product[];
  /**
   * 서버가 한 번에 조립해 내려준 허브 데이터셋(`lib/api/station-hub`).
   * 지점 이름·주소·사진이 이미 들어 있어 마운트 후 재조회가 없다.
   */
  initialHubDataset?: StationHubDataset | null;
  locale: string;
  /** 검색 지역명 — 드로어 헤더 한 줄·거리 기준점 문구에 쓴다. */
  searchQuery: string;
  mapCenter: { lat: number; lng: number };
  mapInitialZoom?: number;
  /** 첫 화면 지도 중심의 나라(서버 계산, 10-05) — 지역·명소 알약이 나라 조회 없이 바로 목록을 받는다. */
  initialLandmarkCountry?: InitialLandmarkCountry | null;
  /**
   * 검색바에서 명소로 검색해 들어온 경우 그 명소(?place=, 09-28) — 허브가 직접 고른 명소(lm)가
   * 없으면 이것을 고른 명소로 쓴다(핀·카메라·명소 칩 선택·거리 기준점).
   */
  searchLandmark?: PlacePoint | null;
  isNearbySearch?: boolean;
  /** 지도 영역 검색(사용자가 지도를 움직여 만든 중심)인가 — 거리 기준점 판정에 쓴다. */
  isMapAreaSearch?: boolean;
  /** 활성 브레이크포인트 — 지도는 한 번에 하나만 마운트한다(중복 과금 방지). */
  mapLayout: "mobile" | "tablet" | "desktop" | undefined;
  checkinDate: string | null;
  checkoutDate: string | null;
  /** 검색 날짜 변경 — 캠핑장 시트의 "날짜 변경"이 검색 페이지와 같은 달력으로 바꾼다. */
  onDateChange?: (checkin: string | null, checkout: string | null) => void;
  /**
   * 기존 검색 필터바를 그려 주는 렌더 프롭. 허브는 필터 축 알약을 필터바의
   * 카테고리 칩 **같은 줄**에 이어 붙이기 위해 `chipsSuffix` 를 넘기고, 상단
   * "필터 N" 배지에 더할 축 개수를 `extraFilterCount` 로 알린다.
   */
  filterBar?: (options: {
    floating: boolean;
    chipsSuffix: React.ReactNode;
    /** 줄 맨 앞의 [지점 | 차종] 토글(10-13) — SearchFilterBar leading 으로 넘긴다 */
    leading: React.ReactNode;
    extraFilterCount: number;
    /** 데스크톱 가로 한 줄(목록·지도 위) — SearchFilterBar inline 으로 넘긴다. */
    inline?: boolean;
  }) => React.ReactNode;
  t: HubTranslate;
  /** 검색 페이지의 정렬 상태·정렬 다이얼로그 열기 — 차종 목록이 같은 정렬을 쓴다. */
  sortBy?: SortOption | null;
  onSortOpen?: () => void;
  /**
   * 캠핑 [장비] 탭(09-29) — 검색 경로의 region 세그먼트와, `products` 가 어느 주소의
   * 결과인지(결과 키). 지금 주소와 키가 다르면 탭을 열 때 택배 장비만 따로 받는다.
   */
  region?: string;
  productsResultKey?: string;
  /**
   * 색인 랜딩의 화면 제목(B1, 서버 계산 — 예: "도쿄 오토바이 렌트"). 있으면 목록 머리가 이 문구의
   * h1 이 되고 "지점 N곳"은 아래 보조 줄(`landingSubline`)로 내려간다. 없으면 지금 머리 그대로.
   */
  landingTitle?: string | null;
  /** 랜딩 보조 줄 — "지점 102곳 · 차종 42 · 역 앞 24시간 무인 픽업"(허브 데이터셋 숫자) */
  landingSubline?: string | null;
  /** 지점·차종 목록 끝의 "함께 찾는 렌탈"(B3) */
  relatedRentals?: RelatedRentals | null;
  /**
   * 지점 페이지 이름 slug — `initialHubDataset.stations` 와 같은 순서(서버 계산). 지점 행의
   * 이름이 이 경로의 `<a href>` 가 된다 — null(페이지 없음)인 지점은 링크하지 않는다.
   */
  stationPageNames?: Array<string | null> | null;
  /**
   * 모델 키 → 모델 허브 경로·이름(서버 계산). 차종 행의 `<a href>`, 차종 시트의 "{이름} 전체
   * 보기 ›" 가 된다 — 없으면 대표 상품.
   */
  modelLinks?: Record<string, HubModelLink> | null;
  /** 색인 랜딩 하단 안내 블록(서버 계산) — 목록 끝, "함께 찾는 렌탈" 바로 위 */
  landingBlocks?: LandingBlocks | null;
}

/**
 * 모빌리티 지점 허브 — 상품 카드 목록 대신 지점(拠点) 중심 화면.
 *
 * **레이아웃은 검색 페이지(상품 모드)와 완전히 같다**: 모바일 = 고정 지도 + 스냅
 * 드로어, 태블릿 = 목록 + 전체화면 지도 토글, 데스크톱 = 58% 목록 열 + 지도 열.
 * 상품 카드 자리에 허브 시트가 들어갈 뿐이다. spec §3-1, §3-2.
 */
export default function StationHub({
  kind = "mobility",
  products,
  initialHubDataset,
  locale,
  searchQuery,
  mapCenter,
  mapInitialZoom,
  initialLandmarkCountry = null,
  searchLandmark = null,
  isNearbySearch,
  isMapAreaSearch,
  mapLayout,
  checkinDate,
  checkoutDate,
  onDateChange,
  filterBar,
  t,
  sortBy = null,
  onSortOpen,
  region,
  productsResultKey,
  landingTitle = null,
  landingSubline = null,
  relatedRentals = null,
  stationPageNames = null,
  modelLinks = null,
  landingBlocks = null,
}: StationHubProps) {
  // 열린 시트·필터 축은 URL 이 정본이다 — 상품 상세에 갔다 뒤로 오면 그대로 복원된다.
  const searchParams = useSearchParams();

  const hubData = useStationHubData(products, locale, initialHubDataset);
  // "이 지역에서 검색"(주소가 MAP_AREA 범위)이면 그 범위 안 지점만 — 목록·핀·차종의
  // "지점 N곳"이 모두 이 집합을 본다(station-bounds). 첫 진입은 지금처럼 전 지점.
  const areaBounds = useMemo(() => mapAreaBoundsOf(searchParams), [searchParams]);
  // 도시·지역·국가 랜딩은 그 범위 지점만 — 제목 보조 줄·메타·ItemList(서버)와 같은 판정
  // (hubScopeFromParams). 도시 범위에 지점이 없으면 전 지점(가까운 순)으로 둔다.
  const regionStationIds = useMemo(
    () =>
      hubScopeStationIds(
        initialHubDataset?.stations,
        hubScopeFromParams(region ?? "", locale, searchParams),
      ),
    [searchParams, initialHubDataset, region, locale],
  );
  const boundedStations = useMemo(
    () =>
      boundHubStations(
        {
          stations: hubData.stations,
          stationsById: hubData.stationsById,
          models: hubData.models,
        },
        areaBounds,
        regionStationIds,
      ),
    [hubData.stations, hubData.stationsById, hubData.models, areaBounds, regionStationIds],
  );
  const {
    facets: hubFacets,
    shippable: hubShippable,
    details: hubDetails,
    loadingDetails: hubLoadingDetails,
  } = hubData;
  const data = useMemo(
    () => ({
      ...boundedStations,
      facets: hubFacets,
      shippable: hubShippable,
      details: hubDetails,
      loadingDetails: hubLoadingDetails,
    }),
    [boundedStations, hubFacets, hubShippable, hubDetails, hubLoadingDetails],
  );
  const { formatWithCodeFromSource } = useCurrency();
  const coords = useGrantedGeolocation();
  const urlState = useMemo(
    () => parseHubParams(searchParams, kind),
    [searchParams, kind],
  );
  const urlKey = hubStateKey(urlState);

  const [mode, setMode] = useState<HubMode>(() =>
    urlState.mode === "catalog" ? "catalog" : "map",
  );
  const [sheet, setSheet] = useState<HubSheet>(() =>
    sheetFromUrl(urlState, data, kind),
  );
  const [axesState, setAxes] = useState<HubAxes>(() => axesFromUrl(urlState));
  // 캠핑은 인원 축만, 모빌리티는 바이크 축만 — URL 에 남은 반대쪽 축(다른 허브에서
  // 넘어온 링크 등)이 지점을 걸러 버리지 않도록 종류에 맞게 읽는다.
  const axes = useMemo(() => axesForKind(axesState, kind), [axesState, kind]);
  // 명소 필터(09-23) — 고르면 거리 기준점이 그 명소가 된다. URL(`lm`)이 정본.
  const [landmarkId, setLandmarkId] = useState<string | null>(
    () => urlState.landmarkId ?? null,
  );
  // 명소 이름·좌표 — 방금 고른 것은 바로 쓰고, URL 로 들어온 것(새로고침·공유 링크)은
  // 서버에서 복원한다(전 세계 명소는 브라우저에 없다).
  const [pickedLandmark, setPickedLandmark] = useState<PlacePoint | null>(null);
  const fetchedLandmark = usePlacePoint(landmarkId, locale);
  // 명소로 검색해 들어온 명소는 허브가 고른 명소가 없을 때의 기본값. 칩에서 해제하면 그
  // 검색이 바뀌기 전까지 다시 켜지 않는다(렌더 중 조정 — 새 검색이면 해제 표시를 푼다).
  const searchLandmarkKey = searchLandmark?.id ?? null;
  const [dismissedSearchLandmark, setDismissedSearchLandmark] = useState<string | null>(null);
  const [seenSearchLandmarkKey, setSeenSearchLandmarkKey] = useState(searchLandmarkKey);
  if (seenSearchLandmarkKey !== searchLandmarkKey) {
    setSeenSearchLandmarkKey(searchLandmarkKey);
    setDismissedSearchLandmark(null);
  }
  const landmark: PlacePoint | null = landmarkId
    ? pickedLandmark && pickedLandmark.id === toWorldId(landmarkId)
      ? pickedLandmark
      : fetchedLandmark
    : searchLandmark && dismissedSearchLandmark !== searchLandmark.id
      ? searchLandmark
      : null;
  const [showFullscreenMap, setShowFullscreenMap] = useState(false);
  // 카드 "지도" 버튼이 보낸 이동 요청(09-29) — 주소에 쓰지 않는 순간 상태. nonce 는
  // 누를 때마다 올라 같은 지점을 다시 눌러도 지도가 다시 간다.
  const [focusRequest, setFocusRequest] = useState<{
    id: string;
    nonce: number;
  } | null>(null);
  const [hoverStore] = useState(createStationHoverStore);
  // "핀이 보이게" 요청(09-29) — 목록엔 지점이 있는데 지도 화면엔 핀이 없을 수 있는 동작
  // (범위 해제·필터 변경) 뒤에 MapView 가 중심 + 가까운 지점 5곳으로 맞춘다.
  const [revealRequest, setRevealRequest] = useState<{
    nonce: number;
    mode: "always" | "ifNoneVisible";
  } | null>(null);
  const requestReveal = useCallback((mode: "always" | "ifNoneVisible") => {
    setRevealRequest((prev) => ({ nonce: (prev?.nonce ?? 0) + 1, mode }));
  }, []);
  // 명소 목록은 지금 보이는 지도 범위를 따라간다(09-28, 검색 페이지와 같은 규칙)
  const [idleView, setIdleView] = useState<ViewBox | null>(null);
  const handleViewIdle = useCallback(
    (v: { center: { lat: number; lng: number }; bounds: ViewBox | null }) => {
      setIdleView(v.bounds ?? boxAround(v.center));
    },
    [],
  );

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const { snap, setSnap, isDragging, handleProps, attachScroll, contentProps } =
    useSnapSheet(SNAP_POINTS, mapContainerRef, {
      // URL 이 "펼침"을 기억하고 있으면 처음부터 100% 로 시작한다(0.15 에서 올라오는
      // 애니메이션이 보이지 않도록). 스냅은 모바일 드로어에만 쓰이므로 sm 이상은 무해.
      // 상품 목록(차종·장비)으로 들어오므로 핀 높이(50%)에서 시작한다(catalogEntrySnap).
      initialSnap: snapFromUrl(urlState) ?? catalogEntrySnap(urlState),
      // 드래그가 스냅에 붙은 뒤 한 번 — 현재 렌더의 시트·축 값을 함께 기록한다.
      onSettle: (value) =>
        writeHubUrl({
          mode,
          sheet,
          axes,
          expanded: value >= 1,
        }),
    });

  // URL(외부 시스템)이 바뀌면 — 뒤로가기 등 — 렌더 중에 상태를 맞춘다.
  // (effect 가 아니라 렌더 중 조정: React 공식 "props 가 바뀔 때 state 조정" 패턴.
  //  복원이므로 여기서는 스크롤을 건드리지 않는다.)
  const [syncedUrl, setSyncedUrl] = useState(urlState);
  if (urlKey !== hubStateKey(syncedUrl)) {
    // 탭·깊이가 바뀐 이동(뒤로가기로 차종 목록에 돌아옴 등)만 "들어온" 것으로 친다 —
    // 드래그로 높이만 바뀐 기록에 반응해 드로어를 50% 로 되돌리지 않도록.
    const arrived =
      syncedUrl.mode !== urlState.mode ||
      hubDepth(syncedUrl) !== hubDepth(urlState);
    setSyncedUrl(urlState);
    setMode(urlState.mode === "catalog" ? "catalog" : "map");
    setSheet(sheetFromUrl(urlState, data, kind));
    setAxes(axesFromUrl(urlState));
    setLandmarkId(urlState.landmarkId ?? null);
    // 드로어 펼침도 URL 이 정본 — 뒤로가기로 돌아오면 그 높이로 되돌린다.
    setSnap(
      snapFromUrl(urlState) ??
        (arrived ? catalogEntrySnap(urlState) : undefined) ??
        SNAP_POINTS[0],
    );
  }

  // 드로어 본문(스크롤 컨테이너) — 시트를 바꿀 때 맨 위로 되돌리기 위해 붙잡아 둔다.
  // 콜백 ref 안에서만 대입한다(렌더 중 ref 접근 금지 규칙).
  const drawerScrollRef = useRef<HTMLDivElement | null>(null);
  const attachDrawerScroll = useCallback(
    (node: HTMLDivElement | null) => {
      drawerScrollRef.current = node;
      attachScroll(node);
    },
    [attachScroll],
  );

  /**
   * 시트가 바뀌면 목록 스크롤을 맨 위로. 모바일은 드로어 본문, 태블릿·데스크톱은
   * 페이지 자체가 스크롤이므로 window — 둘 다 불러도 서로 방해하지 않는다.
   * (effect 가 아니라 시트를 바꾸는 핸들러에서 직접 호출한다.)
   */
  const resetScroll = useCallback(() => {
    drawerScrollRef.current?.scrollTo({ top: 0 });
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  }, []);

  // 거리 기준점: 이미 허용된 현재 위치 — 단, 지도 중심에서 200km 넘게 떨어져 있으면
  // (한국에서 도쿄를 보는 경우) 쓸모가 없으므로 지도 중심으로 되돌린다.
  const geoNearMap = useMemo(() => {
    if (!coords) return false;
    return (
      calculateDistance(
        coords.latitude,
        coords.longitude,
        mapCenter.lat,
        mapCenter.lng,
      ) <= MAX_DISTANCE_KM
    );
  }, [coords, mapCenter.lat, mapCenter.lng]);

  // 기준점 우선순위: 고른 명소 > 허용된 현재 위치 > 지도 중심.
  const origin = useMemo(
    () =>
      landmark
        ? { lat: landmark.lat, lng: landmark.lng }
        : geoNearMap && coords
          ? { lat: coords.latitude, lng: coords.longitude }
          : { lat: mapCenter.lat, lng: mapCenter.lng },
    [landmark, geoNearMap, coords, mapCenter.lat, mapCenter.lng],
  );

  // "지도 영역 검색"은 **이번 세션에서 사용자가 지도를 움직였을 때**만 의도로 친다.
  // 처음부터 bounds 가 박힌 URL(도시 진입 등)은 사용자가 고른 기준점이 아니다.
  // (ref 가 아니라 state 초기화로 잡는다 — 렌더 중 ref 읽기는 레포 ESLint 금지)
  const [mapAreaAtMount] = useState(() => !!isMapAreaSearch);
  const movedMapInSession = !!isMapAreaSearch && !mapAreaAtMount;

  /** "내 주변"을 기본 탭으로 열 근거가 있는가 (§4 리뷰 규칙). */
  const defaultNearby =
    !!landmark || geoNearMap || !!isNearbySearch || movedMapInSession;

  /** 거리 기준점이 사용자 위치가 아니라 지도 중심인가. */
  const originIsMapCenter = !landmark && !geoNearMap;

  /**
   * 지점 목록 정렬(가까운 순 · 지역별) — 칩은 목록 머리 둘째 줄, [지점 | 차종] 토글 오른쪽(10-13).
   * 고른 값은 명소별로 기억하고, 명소가 바뀌면 기본값(기준점이 있으면 가까운 순)부터 다시 연다
   * — 예전에 NearbySheet 를 명소 key 로 새로 만들어 정렬을 되돌리던 것과 같은 동작이다.
   */
  const sortScope = landmarkId ?? "none";
  const [pickedSort, setPickedSort] = useState<{
    scope: string;
    sort: "nearest" | "area";
  } | null>(null);
  const nearbySort: "nearest" | "area" =
    pickedSort?.scope === sortScope
      ? pickedSort.sort
      : defaultNearby
        ? "nearest"
        : "area";

  const rawDistanceKm = useCallback(
    (lat: number, lng: number) =>
      calculateDistance(origin.lat, origin.lng, lat, lng),
    [origin],
  );

  // 표시용 거리 — 기준점이 너무 멀면(다른 나라) 숫자가 잡음이라 아예 빼 버린다.
  const distanceKm = useCallback(
    (lat: number, lng: number): number | null => {
      const km = rawDistanceKm(lat, lng);
      return km <= MAX_DISTANCE_KM ? km : null;
    },
    [rawDistanceKm],
  );

  /**
   * 열린 시트를 현재 히스토리 항목에 기록한다(replaceState — 새 항목을 쌓지 않으므로
   * 허브의 "뒤로"와 브라우저 뒤로가기가 서로 싸우지 않는다). 상품 상세로 나갈 때는
   * 이 URL이 그대로 히스토리에 남아, 뒤로가기가 같은 지점 시트로 돌아온다.
   */
  /**
   * 열린 시트를 URL 에 기록한다.
   *
   * 화면이 바뀌는 전환(지점 시트 열기·모드 전환)은 **pushState** — 그래야 네브바
   * 뒤로가기가 페이지를 떠나지 않고 이전 시트로 돌아온다. 클래스 칩·드로어 높이는
   * replaceState 라 히스토리를 더럽히지 않는다. 판정은 순수 함수 `historyModeFor`.
   */
  const writeHubUrl = useCallback(
    (next: {
      mode: HubMode;
      sheet: HubSheet;
      axes: HubAxes;
      expanded: boolean;
      /** undefined = 지금 URL 의 명소를 그대로 둔다, null = 지운다 */
      landmarkId?: string | null;
    }) => {
      if (typeof window === "undefined") return;
      const previous = parseHubParams(
        new URLSearchParams(window.location.search),
        kind,
      );
      const state: HubUrlState = {
        mode: next.mode === "catalog" ? "catalog" : "stations",
        stationId:
          next.sheet.kind === "bikes" ? next.sheet.stationId : undefined,
        modelKey: next.sheet.kind === "model" ? next.sheet.modelKey : undefined,
        classKey: next.axes.classKey ?? undefined,
        makers: next.axes.makers.length > 0 ? next.axes.makers : undefined,
        types: next.axes.types.length > 0 ? next.axes.types : undefined,
        rides: next.axes.rides.length > 0 ? next.axes.rides : undefined,
        trans: next.axes.trans ?? undefined,
        parties: next.axes.parties.length > 0 ? next.axes.parties : undefined,
        landmarkId:
          next.landmarkId === undefined
            ? previous.landmarkId
            : (next.landmarkId ?? undefined),
        expanded: next.expanded || undefined,
      };
      const search = applyHubParams(window.location.search, state, kind);
      const url = `${window.location.pathname}${search}${window.location.hash}`;

      if (historyModeFor(previous, state) === "push") {
        window.history.pushState({}, "", url);
      } else {
        window.history.replaceState({}, "", url);
      }
    },
    [kind],
  );

  // 가격은 기본 카드와 같은 통화 코드 표기("KRW 13,800") — 기호(₩)는 쓰지 않는다(10-15)
  const formatPrice = useCallback(
    (amount: number, currency?: string) =>
      formatWithCodeFromSource(amount, (currency || "USD") as SupportedCurrency),
    [formatWithCodeFromSource],
  );

  const productHref = useCallback(
    (productId: string, stationId?: string) => {
      const params = new URLSearchParams();
      if (stationId) params.set("branch", stationId);
      if (checkinDate) params.set("checkin", checkinDate);
      if (checkoutDate) params.set("checkout", checkoutDate);
      const query = params.toString();
      return `/${locale}/products/${productId}${query ? `?${query}` : ""}`;
    },
    [locale, checkinDate, checkoutDate],
  );

  // 지점 id → 페이지 경로 — 서버가 보낸 이름 slug 배열을 데이터셋 지점 순서로 되살린다(10-16).
  const stationPaths = useMemo(
    () =>
      initialHubDataset && stationPageNames
        ? stationPathsFromNameSlugs(
            locale,
            initialHubDataset.stations.map((station) => station.id),
            stationPageNames,
          )
        : null,
    [initialHubDataset, stationPageNames, locale],
  );
  const stationHref = useCallback(
    (stationId: string) => stationPaths?.[stationId],
    [stationPaths],
  );
  const modelLink = useCallback(
    (modelKey: string) => modelLinks?.[modelKey],
    [modelLinks],
  );

  // 실제로 존재하는 클래스만, BIKE_CLASSES 순서로.
  const presentClasses = useMemo<BikeClass[]>(() => {
    const keys = new Set<string>();
    for (const station of data.stations) {
      for (const cls of station.classes) keys.add(cls.key);
    }
    return BIKE_CLASSES.filter((cls) => keys.has(cls.key));
  }, [data.stations]);

  // 데이터셋에 실제로 있는 제조사·타입·라이딩·변속 — 알약을 그릴지의 근거.
  const presentFacets = useMemo(
    () => collectPresentFacets(data.facets.values()),
    [data.facets],
  );

  // 데이터셋에 실제로 있는 캠핑 인원(오름차순) — 인원 알약을 그릴지의 근거. 캠핑만.
  const presentParties = useMemo(
    () =>
      kind === "camping"
        ? collectPartySizes(
            Array.from(data.facets.values(), (facets) => facets.partySize),
          )
        : [],
    [kind, data.facets],
  );

  const facetsOf = useCallback(
    (productId: string) => data.facets.get(productId),
    [data.facets],
  );

  /**
   * 축을 모두 만족하는 상품만(축 사이 AND · 축 안 OR). 축이 비면 원본 배열
   * 그대로 돌려 준다(참조 동일).
   */
  const visibleProducts = useCallback(
    (station: StationSummary): Product[] => {
      if (isAxesEmpty(axes)) return station.products;
      return station.products.filter((product) => {
        const facets = data.facets.get(product.id);
        return facets ? matchesAxes(facets, axes) : false;
      });
    },
    [axes, data.facets],
  );

  // 지점 = 축을 만족하는 상품이 1개 이상인 곳. 축이 비면 원본 배열 그대로.
  const stations = useMemo(
    () =>
      isAxesEmpty(axes)
        ? data.stations
        : data.stations.filter(
            (station) => visibleProducts(station).length > 0,
          ),
    [data.stations, axes, visibleProducts],
  );

  // 카탈로그도 같은 술어 — 모델의 상품 중 하나라도 축을 만족하면 남는다.
  const models = useMemo(
    () =>
      isAxesEmpty(axes)
        ? data.models
        : data.models.filter((model) =>
            model.products.some((product) => {
              const facets = data.facets.get(product.id);
              return facets ? matchesAxes(facets, axes) : false;
            }),
          ),
    [data.models, data.facets, axes],
  );

  const open = useCallback(
    (next: HubSheet, opts?: { fromPin?: boolean }) => {
      // 시트를 열면 "지도" 버튼·호버로 켠 핀 강조는 끝난다(열린 지점은 선택 핀이 된다)
      setFocusRequest(null);
      hoverStore.set(null);
      setSheet(next);
      // 목록에서 지점·차종을 고르면 끝까지 펼친다(목록이 본문이다). 지도에서 핀을
      // 고르면 중간 높이 — 지도와 선택 핀을 함께 보여 준다(상품 드로어와 같은 규칙).
      const nextSnap =
        next.kind === "nearby"
          ? SNAP_POINTS[0]
          : opts?.fromPin && next.kind === "bikes"
            ? PIN_SNAP
            : 1;
      setSnap(nextSnap);
      setShowFullscreenMap(false);
      writeHubUrl({
        mode:
          next.kind === "catalog" || next.kind === "model" ? "catalog" : "map",
        sheet: next,
        axes,
        expanded: nextSnap >= 1,
      });
      resetScroll();
    },
    [setSnap, writeHubUrl, axes, resetScroll, hoverStore],
  );

  const handleModeChange = useCallback(
    (next: HubMode) => {
      const nextSheet: HubSheet =
        next === "map" ? { kind: "nearby" } : { kind: "catalog" };
      setFocusRequest(null);
      setMode(next);
      setSheet(nextSheet);
      setSnap(next === "map" ? SNAP_POINTS[0] : 1);
      if (next === "catalog") setShowFullscreenMap(false);
      writeHubUrl({
        mode: next,
        sheet: nextSheet,
        axes,
        expanded: next !== "map",
      });
      resetScroll();
    },
    [setSnap, writeHubUrl, axes, resetScroll],
  );

  /** 필터 축 변경 — 즉시 적용하고 URL 에도 남겨(replace) 뒤로가기로 돌아와도 유지. */
  const handleAxesChange = useCallback(
    (next: HubAxes) => {
      setAxes(next);
      writeHubUrl({ mode, sheet, axes: next, expanded: snap >= 1 });
      // 새로 걸린(풀린) 지점이 모두 화면 밖이면 지도를 가까운 지점까지 넓힌다
      requestReveal("ifNoneVisible");
    },
    [mode, sheet, snap, writeHubUrl, requestReveal],
  );

  /**
   * 명소 적용 — 기준점을 바꾸고 지점 목록(가까운 순)으로 돌아간다. 차종 목록·카탈로그를
   * 보고 있었어도 "이 명소 근처 지점"을 보여 주는 것이 고른 뜻이다.
   */
  const handleLandmarkChange = useCallback(
    (point: PlacePoint | null) => {
      const id = point?.id ?? null;
      setPickedLandmark(point);
      setLandmarkId(id);
      if (!point && searchLandmark) setDismissedSearchLandmark(searchLandmark.id);
      if (!id) {
        writeHubUrl({ mode, sheet, axes, expanded: snap >= 1, landmarkId: null });
        return;
      }
      setMode("map");
      setSheet({ kind: "nearby" });
      writeHubUrl({
        mode: "map",
        sheet: { kind: "nearby" },
        axes,
        expanded: snap >= 1,
        landmarkId: id,
      });
      resetScroll();
    },
    [mode, sheet, axes, snap, writeHubUrl, resetScroll, searchLandmark],
  );

  /**
   * 카드의 "지도" 버튼(09-29) — 시트를 열지 않고 지도에서 그 지점을 보여 준다.
   * 모바일: 드로어를 맨 아래 스냅으로 내린다(목록 스크롤은 그대로 — 다시 올리면 그 자리).
   * 태블릿: 전체화면 지도로 바꾼다. 이동·확대·핀 강조는 MapView 가 요청을 받아 한다.
   * 핀을 누르면 그때 지금처럼 지점 시트가 열린다.
   */
  const focusStationOnMap = useCallback(
    (stationId: string) => {
      setFocusRequest((prev) => ({
        id: stationId,
        nonce: (prev?.nonce ?? 0) + 1,
      }));
      if (mapLayout === "tablet") {
        setShowFullscreenMap(true);
        return;
      }
      setSnap(SNAP_POINTS[0]);
      writeHubUrl({ mode, sheet, axes, expanded: false });
    },
    [mapLayout, setSnap, writeHubUrl, mode, sheet, axes],
  );

  /**
   * "전체 지점 보기"(빈 화면, 09-29) — 지도 영역 범위(ne/sw + location_search=MAP_AREA)를
   * 주소에서 지운다. "이 지역에서 검색"과 같은 얕은 주소 변경이라 페이지·지도는 그대로이고,
   * 범위가 사라지면 boundHubStations 가 전 지점을 돌려준다.
   */
  const clearArea = useCallback(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    MAP_AREA_KEYS.forEach((key) => params.delete(key));
    const query = params.toString();
    replaceSearchUrlShallow(
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
    );
    resetScroll();
    // 범위 안엔 지점이 없었으므로 지금 화면에도 핀이 없다 — 중심 + 가까운 5곳으로 맞춘다
    requestReveal("always");
  }, [resetScroll, requestReveal]);

  // 지도가 늘 옆에 보이는 데스크톱은 "지도" 버튼 대신 카드 호버로 핀을 강조한다.
  const isDesktopTree = (mapLayout ?? "desktop") === "desktop";

  // ===== 캠핑 [장비] 탭(09-29) =====
  // 허브 장비 = 지점 목록과 같은 캠핑장 집합(범위 적용)의 상품, 목록과 같은 가까운 순.
  // 메모리에서 바로 — 탭·지역을 바꿔도 요청이 없다. 탭이 열렸을 때만 계산한다.
  const gearTabOpen = kind === "camping" && sheet.kind === "catalog";
  const hubGear = useMemo(() => {
    if (!gearTabOpen) return [];
    const ordered = [...stations].sort(
      (a, b) => rawDistanceKm(a.lat, a.lng) - rawDistanceKm(b.lat, b.lng),
    );
    return collectHubGear(ordered);
  }, [gearTabOpen, stations, rawDistanceKm]);
  // 택배 등 허브에 없는 장비 — 일반 검색 결과에서만 알 수 있다(useGearExtras).
  const allHubProductIds = useMemo(
    () => (gearTabOpen ? hubProductIds(hubData.stations) : new Set<string>()),
    [gearTabOpen, hubData.stations],
  );
  const gearExtrasSource = useGearExtras({
    enabled: gearTabOpen,
    locale,
    region,
    searchParams,
    pageResultKey: productsResultKey,
    pageProducts: products,
  });
  // 범위 밖 캠핑장에만 있는 택배 장비 — 지역과 상관없이 받을 수 있으므로 남긴다.
  const shippedGear = useMemo(
    () =>
      gearTabOpen
        ? shippableHubGear(
            hubData.stations,
            hubData.shippable,
            new Set(hubGear.map((entry) => entry.product.id)),
          )
        : [],
    [gearTabOpen, hubData.stations, hubData.shippable, hubGear],
  );
  // 인원 축은 [장비] 탭에도 같은 술어로 — 허브 장비는 데이터셋 값, 허브에 없는 택배
  // 장비는 검색 결과의 태그·이름으로 판정한다(캠핑장 시트·행과 같은 기준).
  const gearEntries = useMemo(() => {
    const entries = [
      ...hubGear,
      ...shippedGear,
      ...extraGear(gearExtrasSource.products, allHubProductIds),
    ];
    if (axes.parties.length === 0) return entries;
    return entries.filter(({ product }) =>
      matchesParty(
        data.facets.get(product.id)?.partySize ??
          resolvePartySize(product.tags, product.name),
        axes.parties,
      ),
    );
  }, [
    hubGear,
    shippedGear,
    gearExtrasSource.products,
    allHubProductIds,
    axes.parties,
    data.facets,
  ]);

  const markers = useMemo(
    () =>
      stations
        .filter((station) => station.lat && station.lng)
        .map((station) => {
          const detail = data.details.get(station.id);
          return {
            id: station.id,
            lat: station.lat,
            lng: station.lng,
            title: detail?.name || station.city,
            // station variant 핀은 **아이콘 전용** — 가격을 싣지 않는다(설계 §3-3).
            price: 0,
            image: detail?.imageUrl || station.image || "",
            location: {
              lat: station.lat,
              lng: station.lng,
              address: detail?.address || station.city,
            },
            categoryCode: stationCategoryCode(kind),
          };
        }),
    [stations, data.details, kind],
  );

  const selectedStationId =
    sheet.kind === "bikes" ? sheet.stationId : undefined;

  const hubValue = useMemo(
    () => ({
      kind,
      locale,
      t,
      data,
      origin,
      originLabel: searchQuery,
      defaultNearby,
      originIsMapCenter,
      landmark,
      landmarkLabel: landmark?.name ?? "",
      classes: presentClasses,
      presentFacets,
      axes,
      setAxes: handleAxesChange,
      sortBy,
      onSortOpen,
      facetsOf,
      visibleProducts,
      open,
      productHref,
      stationHref,
      modelLink,
      distanceKm,
      rawDistanceKm,
      checkinDate,
      checkoutDate,
      onDateChange,
      formatPrice,
      areaBounded: areaBounds !== null,
      clearArea,
      focusStation: isDesktopTree ? undefined : focusStationOnMap,
      hoverStation: isDesktopTree ? hoverStore.set : undefined,
    }),
    [
      kind,
      locale,
      t,
      data,
      origin,
      searchQuery,
      defaultNearby,
      originIsMapCenter,
      landmark,
      presentClasses,
      presentFacets,
      axes,
      handleAxesChange,
      sortBy,
      onSortOpen,
      facetsOf,
      visibleProducts,
      open,
      productHref,
      stationHref,
      modelLink,
      distanceKm,
      rawDistanceKm,
      checkinDate,
      checkoutDate,
      onDateChange,
      formatPrice,
      areaBounds,
      clearArea,
      isDesktopTree,
      focusStationOnMap,
      hoverStore,
    ],
  );

  /**
   * 목록·카탈로그 단계인가. 지점(차종 목록)·모델 시트에서는 "지점 N곳" 헤더와
   * [지점 | 차종] 토글을 내린다 — 지금 보는 것이 목록이 아니라 한 지점/한 차종이기
   * 때문이다. 허브 자체 "뒤로"는 어디에도 두지 않는다(09-23): 모바일과 마찬가지로
   * 브라우저·네브바 뒤로가기가 그 일을 한다(시트 열기가 히스토리 항목을 쌓는다).
   */
  const isListLevel = sheet.kind === "nearby" || sheet.kind === "catalog";

  const renderSheet = () => {
    switch (sheet.kind) {
      case "nearby":
        // 명소가 바뀌면 정렬 상태를 새로(가까운 순) 시작한다
        return (
          <NearbySheet
            key={landmarkId ?? "none"}
            stations={stations}
            sort={nearbySort}
          />
        );
      case "bikes": {
        const station = data.stationsById.get(sheet.stationId);
        if (!station) return null;
        return kind === "camping" ? (
          <CampingStationSheet station={station} />
        ) : (
          <StationBikesSheet station={station} />
        );
      }
      case "catalog":
        return kind === "camping" ? (
          <GearSheet entries={gearEntries} pending={gearExtrasSource.pending} />
        ) : (
          <CatalogSheet models={models} />
        );
      case "model": {
        const model = data.models.find((m) => m.key === sheet.modelKey);
        return model ? <ModelSheet model={model} /> : null;
      }
      default:
        return null;
    }
  };

  /**
   * 헤더 줄 오른쪽의 세그먼트 컨트롤 — 전폭 탭 바를 대신한다. 기본 탭이 앞:
   * 오토바이 [차종 | 지점](10-06), 캠핑 [캠핑장 | 장비].
   */
  const stationsOption = {
    value: "map" as HubMode,
    label: t(hubKey(kind, "modeStations")),
  };
  const catalogOption = {
    value: "catalog" as HubMode,
    label: t(hubKey(kind, "modeCatalog")),
  };
  // 회색 알약 토글(10-13) — 필터 줄 맨 앞. 바깥 틀 gray-200, 선택 = 흰 알약·굵게.
  // 높이는 옆 알약과 같게(모바일 34 · 데스크톱 40 = 트리거 28/34 + 위아래 3px).
  const modeToggle = (floating: boolean) => (
    <SegmentedTabs
      value={mode}
      onChange={handleModeChange}
      floating={floating}
      listClassName="h-auto gap-0.5 rounded-full bg-gray-200 p-[3px]"
      triggerClassName="h-7 min-w-[64px] rounded-full px-3 py-0 text-[13px] font-medium text-gray-600 data-[state=active]:font-semibold data-[state=active]:text-gray-900 data-[state=active]:shadow-[0_1px_2px_rgba(0,0,0,0.08)] mapShow:h-[34px] mapShow:min-w-[80px] mapShow:px-4 mapShow:text-sm"
      // 오토바이·캠핑 모두 [차종|지점] · [장비|캠핑장] — 상품 탭이 먼저이자 기본(10-15)
      options={[catalogOption, stationsOption]}
    />
  );

  /**
   * 카테고리 칩 자리에 이어 붙일 필터 축 알약 — 카테고리 칩을 숨기므로
   * (hideCategoryChips) 알약이 줄의 시작이다. 선택은 즉시 적용된다.
   */
  const chipsSuffix = (floating: boolean) => (
    <>
      {/* 명소가 줄의 맨 앞 — "어디 근처"가 차종 조건보다 먼저 정해진다(에어비앤비 방식) */}
      <LandmarkPill
        view={idleView ?? boxAround(mapCenter)}
        initialCountry={initialLandmarkCountry}
        value={landmark}
        onChange={handleLandmarkChange}
        floating={floating}
        locale={locale}
        t={t}
      />
      {/* 캠핑 = [명소] [인원], 모빌리티 = [명소] [제조사·배기량·타입·라이딩·변속] —
          반대쪽 축 값을 비워 넘겨 서로의 알약이 나오지 않는다 */}
      <AxisPills
        axes={axes}
        onChange={handleAxesChange}
        classes={kind === "mobility" ? presentClasses : []}
        present={kind === "mobility" ? presentFacets : NO_PRESENT_FACETS}
        parties={presentParties}
        floating={floating}
        locale={locale}
        t={t}
      />
    </>
  );

  /** 필터바(+ 합쳐진 알약 한 줄). 지도 위에 띄울 때만 floating, 데스크톱은 inline. */
  const renderFilterBar = (floating: boolean, inline = false) =>
    filterBar?.({
      floating,
      inline,
      chipsSuffix: chipsSuffix(floating),
      leading: modeToggle(floating),
      extraFilterCount: countActiveAxes(axes) + (landmark ? 1 : 0),
    });

  /**
   * 목록 머리(10-13) — 일반 검색 목록과 같은 주황 막대 제목(지점 N곳 / 차종 N) 한 줄,
   * 오른쪽에 정렬 칩(가까운 순 · 지역별). [지점 | 차종] 토글은 필터 줄 맨 앞으로 갔다.
   * 정렬 칩은 지점 보기이고 거리 기준점이 있을 때만.
   */
  const showSortChips = mode === "map" && sheet.kind === "nearby" && defaultNearby;
  const listTitleClass =
    "min-w-0 truncate text-lg font-bold text-gray-900 mapShow:text-xl";
  /**
   * `asHeading=false` 는 모바일 미리보기용 — 레이아웃 판정 전에는 데스크톱 트리가 같은 머리를
   * h1 로 함께 SSR 하므로, 미리보기 쪽은 같은 모양의 `<p>` 로 그려 h1 이 한 벌만 남게 한다.
   * 판정이 모바일이면 데스크톱 트리가 빠지고 이 자리가 h1 이 된다(제목 요소만 바뀌어 높이 그대로).
   */
  const renderListHeader = (asHeading: boolean) => (
    <div className={`flex items-center justify-between gap-3 ${HUB_GUTTER}`}>
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <div className="h-5 w-1 shrink-0 rounded-full bg-orange-500" />
          {/* 색인 랜딩이면 `<title>` 의 검색어 모양 h1(B1) — 숫자는 보조 줄로 */}
          {landingTitle ? (
            asHeading ? (
              <h1 className={listTitleClass}>{landingTitle}</h1>
            ) : (
              <p className={listTitleClass}>{landingTitle}</p>
            )
          ) : (
            (() => {
              const countTitle =
                mode === "map"
                  ? t(hubKey(kind, "stationsN"), { n: stations.length })
                  : t(hubKey(kind, "catalogHead"), {
                      // 캠핑 [장비] = 허브 장비 + 택배 장비(받은 만큼)
                      n: kind === "camping" ? gearEntries.length : models.length,
                    });
              return asHeading ? (
                <h2 className={listTitleClass}>{countTitle}</h2>
              ) : (
                <p className={listTitleClass}>{countTitle}</p>
              );
            })()
          )}
        </div>
        {landingTitle && landingSubline ? (
          <p className="mt-0.5 truncate pl-3 text-[13px] text-gray-500">{landingSubline}</p>
        ) : null}
      </div>
      {showSortChips ? (
        <div className="flex shrink-0 items-center gap-1.5">
          <Chip
            label={t("hub.sortNearest")}
            selected={nearbySort === "nearest"}
            onClick={() => setPickedSort({ scope: sortScope, sort: "nearest" })}
          />
          <Chip
            label={t("hub.area")}
            selected={nearbySort === "area"}
            onClick={() => setPickedSort({ scope: sortScope, sort: "area" })}
          />
        </div>
      ) : null}
    </div>
  );

  const mapProps = {
    products: markers,
    center: mapCenter,
    initialZoom: mapInitialZoom,
    isNearbySearch,
    locale,
    variant: "station" as const,
    onMarkerClick: (id: string) =>
      open({ kind: "bikes", stationId: id }, { fromPin: true }),
    selectedMarkerId: selectedStationId,
    focusStation: focusRequest,
    revealRequest,
    hoverStore,
    onViewIdle: handleViewIdle,
    landmark: landmark
      ? {
          lat: landmark.lat,
          lng: landmark.lng,
          label: landmark.name,
        }
      : null,
  };

  const isMobileListMode = snap >= 1;

  /**
   * 지점·차종 목록 끝의 랜딩 안내 블록 + "함께 찾는 렌탈"(B3) — 한 지점·한 차종 시트에서는 내린다.
   * 트리는 하나만 마운트되고(activeTree) 모바일 미리보기에서는 부르지 않으므로 한 벌뿐이다.
   */
  const relatedNav = (className: string, gutter: "hubMobile" | "padded") =>
    isListLevel && (landingBlocks || relatedRentals) ? (
      <>
        {landingBlocks ? (
          <LandingBlocksSection
            data={landingBlocks}
            locale={locale}
            gutter={gutter}
          />
        ) : null}
        {relatedRentals ? (
          <RelatedRentalsNav data={relatedRentals} className={className} />
        ) : null}
      </>
    ) : null;

  /**
   * **목록 본문은 한 벌만 그린다.** 세 레이아웃(모바일 드로어 · 태블릿 · 데스크톱)이
   * 각자 목록을 렌더하면 같은 수백 행이 3벌 SSR 되어 HTML 이 MB 단위로 불어난다.
   * 드로어(fixed)와 목록 열은 부모 자체가 달라 한 DOM 노드로 합칠 수 없으므로,
   * **활성 브레이크포인트의 트리만** 마운트한다. SSR·첫 클라이언트 렌더에서는
   * `mapLayout` 이 아직 undefined 이므로 데스크톱 트리를 그리고(하이드레이션 일치),
   * 모바일 첫 화면이 비지 않도록 모바일 트리도 **미리보기**로 함께 그린다(10-15, sm:hidden
   * 이라 sm 이상에서는 보이지 않는다). 미리보기는 지도 없이(회색 칸) 같은 스냅 높이의 드로어에
   * 목록 머리 + 앞 몇 행만 — 판정이 모바일이면 같은 노드가 그대로 진짜 드로어가 되어(재마운트
   * 없음) 높이가 튀지 않고, 지도는 최종 높이의 칸에 처음 마운트된다(카메라 패딩 일치).
   */
  const activeTree = mapLayout ?? "desktop";
  const isMobilePreview = mapLayout === undefined;
  const showMobileTree = mapLayout === "mobile" || isMobilePreview;

  /** 드로어 높이를 바꾸는 유일한 창구 — 바꾼 값을 URL 에도 남긴다. */
  const applySnap = (value: number) => {
    setSnap(value);
    writeHubUrl({ mode, sheet, axes, expanded: value >= 1 });
  };

  return (
    <HubProvider value={hubValue}>
      {/* Mobile (< 640px): Map + Drawer — 상품 모드와 같은 고정 컨테이너.
          레이아웃 판정 전에는 미리보기로 그린다(isMobilePreview, 위 activeTree 설명). */}
      {showMobileTree ? (
        <div
          ref={mapContainerRef}
          data-search-drawer="true"
          style={{
            // navbar = py-2 + 두 줄 검색 알약 h-14 = 4.5rem (검색 페이지 모바일 트리와 같은 값)
            top: `calc(4.5rem + var(--loc-safe-area-inset-top))`,
          }}
          className={`sm:hidden fixed left-0 right-0 bottom-safe-nav flex flex-col ${
            snap > 0.5 ? "z-40" : "z-30"
          }`}
        >
          {/* 지도 영역 - drawer 높이를 뺀 나머지 영역 차지. 지도가 뜨기 전(미리보기·로딩)은
              MapView 로딩 화면과 같은 회색 칸 */}
          <div
            className={`w-full shrink-0 overflow-hidden relative bg-gray-100 ${
              isDragging ? "" : "transition-all duration-300 ease-out"
            }`}
            style={{
              height: `${100 - snap * 100}%`,
              minHeight: "0px",
            }}
          >
            {mapLayout === "mobile" && (
              <HubMap {...mapProps} cameraPaddingTop={FLOATING_CHROME_PX} />
            )}

            {/* 지도 위 필터 레이어 — 상품 모드와 같은 위치.
              카테고리 칩과 클래스 칩은 필터바 안에서 **한 줄**로 이어진다. */}
            <div className="absolute left-3 right-3 top-3 z-10 pointer-events-none">
              {renderFilterBar(true)}
            </div>
          </div>

          {/* Station Sheet Drawer */}
          <div
            data-slot="drawer-content"
            className={`bg-white overflow-hidden shrink-0 flex flex-col ${
              snap >= 1
                ? ""
                : "rounded-t-2xl shadow-[0_-4px_20px_rgba(0,0,0,0.15)]"
            } ${snap > 0.5 ? "z-40" : "z-30"} ${
              isDragging ? "" : "transition-all duration-300 ease-out"
            }`}
            style={{ height: `${snap * 100}%` }}
          >
            {/* Drawer handle - 드래그 가능 (100%일 때 숨김) */}
            <div
              className={`flex justify-center py-3 cursor-grab active:cursor-grabbing touch-none select-none ${
                snap >= 1 ? "hidden" : ""
              }`}
              {...handleProps}
            >
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>

            {/* 드로어 머리 — 필터바(100%일 때) · 헤더 줄이 한 덩어리로 고정되고,
              그 아래 목록이 자기 스크롤 영역에서 움직인다(행이 헤더 밑으로 비치지 않게).
              여백은 세 줄이 같은 거터(모바일 px-3 = 지도 위 left-3)를 쓰고, 알약→칩(필터바 내부)
              · 칩→헤더 mt-4 · 헤더→목록 mt-2 로 맞춘다. */}
            {/* 모바일에는 허브 자체 "뒤로"를 두지 않는다 — 네브바 뒤로가기가 그 일을
                한다(시트 열기가 히스토리 항목을 쌓으므로 페이지를 떠나지 않는다).
                머리에 담을 것이 없으면 블록도 만들지 않는다.
                고정 블록은 선 대신 아래 여백(pb-2)으로 목록과 나눈다(09-22). */}
            {isListLevel || snap >= 1 ? (
              <div className="shrink-0 bg-white pb-2">
                {snap >= 1 ? renderFilterBar(false) : null}
                {isListLevel ? (
                  <div className={snap >= 1 ? "mt-4" : ""}>
                    {renderListHeader(!isMobilePreview)}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div
              ref={attachDrawerScroll}
              className={`w-full min-h-0 flex-1 overscroll-contain pt-2 pb-8 scrollbar-hide ${
                snap <= SNAP_POINTS[0]
                  ? "overflow-hidden touch-none"
                  : "overflow-y-auto"
              }`}
              {...contentProps}
            >
              {/* 미리보기는 앞 몇 행만 — 데스크톱 트리가 이미 같은 목록을 서버 렌더한다 */}
              <WindowCapProvider
                value={isMobilePreview ? MOBILE_PREVIEW_ROWS : null}
              >
                <HubDensityProvider value="compact">
                  {renderSheet()}
                </HubDensityProvider>
              </WindowCapProvider>
              {isMobilePreview ? null : relatedNav("px-4", "hubMobile")}
            </div>
          </div>

          {/* 플로팅 목록/지도 토글 버튼 — 상품 모드와 같은 동작 */}
          {isMobileListMode ? (
            <button
              onClick={() => applySnap(SNAP_POINTS[0])}
              className="fixed right-4 z-60 bg-gray-900 text-white p-2.5 rounded-full shadow-lg"
              style={{
                bottom: "calc(5rem + var(--loc-safe-area-inset-bottom))",
              }}
              title={t("viewMap")}
            >
              <Map className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={() => applySnap(1)}
              className={`absolute right-4 z-60 bg-gray-900 text-white p-2.5 rounded-full shadow-lg ${
                isDragging ? "" : "transition-all duration-300 ease-out"
              }`}
              style={{ bottom: `${snap * 100 + 2}%` }}
              title={t("viewList")}
            >
              <List className="w-5 h-5" />
            </button>
          )}
        </div>
      ) : null}

      {/* Tablet (640px - 950px): 목록 + 전체화면 지도 토글 — 상품 모드와 같은 구조 */}
      {activeTree === "tablet" ? (
        <div className="hidden sm:block mapShow:hidden w-full relative">
          {!showFullscreenMap ? (
            <>
              <div className="p-6 bg-white pb-24">
                {/* 허브의 필터바(+합쳐진 칩 줄) — 페이지 상단 바는 허브에서 끈다 */}
                {renderFilterBar(false)}
                {/* 지점 시트: 필터 줄과 예약 안내 사이 16px — 데스크톱(sticky pb-4)·
                    모바일 드로어(머리 pb-2 + 목록 pt-2)와 같은 간격(09-23). */}
                {isListLevel ? (
                  <div className="mt-6">{renderListHeader(true)}</div>
                ) : (
                  <div className="h-4" aria-hidden />
                )}
                <HubDensityProvider value="compact">
                  {renderSheet()}
                </HubDensityProvider>
                {relatedNav("-mx-6 px-6", "padded")}
              </div>

              <button
                onClick={() => setShowFullscreenMap(true)}
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 bg-gray-900 text-white px-6 py-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
              >
                <Map className="w-5 h-5" />
                {t("viewMap")}
              </button>
            </>
          ) : (
            <>
              <div className="h-[calc(100vh-156px-var(--loc-safe-area-inset-top))] w-full">
                {mapLayout === "tablet" && <HubMap {...mapProps} />}
              </div>

              <button
                onClick={() => setShowFullscreenMap(false)}
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 bg-gray-900 text-white px-6 py-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
              >
                <List className="w-5 h-5" />
                {t("viewList")}
              </button>
            </>
          )}
        </div>
      ) : null}

      {/* Desktop (960px+): 필터는 목록·지도 위를 가로지르는 한 줄(09-23, 에어비앤비 방식),
          그 아래 목록 열 + 지도 열. 필터 줄과 지도는 네브바 아래에 붙어 있다. */}
      {activeTree === "desktop" ? (
        <div className="hidden mapShow:block w-full">
          <div className="sticky top-[calc(81px+var(--loc-safe-area-inset-top))] z-40 border-b border-gray-200 bg-white px-6 py-3">
            {renderFilterBar(false, true)}
          </div>
          <div className="flex w-full">
            {/* 목록 폭은 일반 검색 분할과 같은 규칙 — 960–1279px 58%, 1280px+ 반반(09-23).
                바꿀 때는 SearchPageClient 의 데스크톱 분할과 함께 바꾼다. */}
            <div className="bg-white w-[58%] xl:w-1/2 max-w-300 min-w-0">
              {/* 필터 줄 아래 16px — 모바일·태블릿과 같은 간격(예약 안내·목록 머리 공통) */}
              <div className="px-6 pt-4 pb-6 bg-white">
                {isListLevel ? <div className="mb-2">{renderListHeader(true)}</div> : null}
                <HubDensityProvider value="wide">
                  {renderSheet()}
                </HubDensityProvider>
                {relatedNav("-mx-6 px-6", "padded")}
              </div>
            </div>

            <div className="flex-1">
              {/* 81px 네브바 + 65px 필터 줄(py-3 · 40px 버튼 · 선 1px) 아래에 붙는다 */}
              <div className="sticky top-[calc(146px+var(--loc-safe-area-inset-top))] h-[calc(100vh-146px-var(--loc-safe-area-inset-top))] p-6">
                <div className="h-full rounded-3xl overflow-hidden shadow-sm ring-1 ring-gray-100">
                  {mapLayout === "desktop" && <HubMap {...mapProps} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </HubProvider>
  );
}
